import React, { useEffect, useRef } from "react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

gsap.registerPlugin(ScrollTrigger);

export default function TextReveal({
  children,
  as: Component = "h2",
  className = "",
  delay = 0,
  stagger = 0.04,
  duration = 0.9,
  by = "words", // 'words' or 'chars'
}) {
  const containerRef = useRef(null);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;

    const prefersReducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (prefersReducedMotion) {
      gsap.set(el.querySelectorAll(".reveal-token"), { y: "0%", opacity: 1 });
      return;
    }

    const tokens = el.querySelectorAll(".reveal-token");

    const anim = gsap.fromTo(
      tokens,
      {
        yPercent: 120,
        opacity: 0,
        rotateX: -15,
      },
      {
        yPercent: 0,
        opacity: 1,
        rotateX: 0,
        duration: duration,
        stagger: stagger,
        delay: delay,
        ease: "power3.out",
        scrollTrigger: {
          trigger: el,
          start: "top 88%",
          toggleActions: "play none none none",
          once: true,
        },
      }
    );

    return () => {
      if (anim.scrollTrigger) anim.scrollTrigger.kill();
      anim.kill();
    };
  }, [delay, stagger, duration]);

  // If children is a string, split into tokens
  if (typeof children === "string") {
    const words = children.split(" ");

    return (
      <Component ref={containerRef} className={`${className} overflow-hidden`} style={{ perspective: "1000px" }}>
        {words.map((word, wordIndex) => (
          <span key={wordIndex} className="inline-block overflow-hidden mr-[0.25em] align-top">
            {by === "chars" ? (
              word.split("").map((char, charIndex) => (
                <span key={charIndex} className="reveal-token inline-block will-change-transform">
                  {char}
                </span>
              ))
            ) : (
              <span className="reveal-token inline-block will-change-transform">
                {word}
              </span>
            )}
          </span>
        ))}
      </Component>
    );
  }

  // If children has nested elements, wrap as single block
  return (
    <Component ref={containerRef} className={`${className} overflow-hidden`} style={{ perspective: "1000px" }}>
      <span className="reveal-token inline-block will-change-transform">
        {children}
      </span>
    </Component>
  );
}
